import ConfirmDialog from "../../../../shared/component/ConfirmDialog";
import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  LinearProgress,
  Card,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { uploadFileToFolder } from "../services/uploadFile";

import FileCard from "./FileCard";
import useViewSize from "../hooks/useViewSize";
import ViewSizeSelector from "./ViewSizeSelector";
import FilePreviewDataset from "./FilePreviewDataset";
import useDeleteFile from "../hooks/useDeleteFile";

export default function FolderView({ folder, onFolderSelect, onFolderUpdate }) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [viewSize, setViewSize] = useState("medium");

  const { getGridSizes } = useViewSize(viewSize);

  // New state for file preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const { deleteFile } = useDeleteFile();

  // New state for confirmation dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  if (!folder) {
    return <Typography>No folder selected.</Typography>;
  }

  const { files = [] } = folder;

  const handleDeleteRequest = (file) => {
    setFileToDelete(file);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!fileToDelete) return;
    setConfirmOpen(false);
    try {
      await deleteFile(fileToDelete);
      if (onFolderUpdate) {
        const folders = await queryClient.fetchQuery({
          queryKey: ["categoryFolders"],
        });
        const updatedFolder = folders.find(
          (f) => f.folderId === folder.folderId
        );
        if (updatedFolder) {
          onFolderUpdate(updatedFolder);
        }
      }
    } catch (error) {
      console.error("Error deleting file:", error);
    } finally {
      setFileToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setFileToDelete(null);
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null; // reset file input
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadError(null);
    try {
      await uploadFileToFolder(file, folder.folderId, (progress) => {
        setUploadProgress(progress);
      });
      setUploadProgress(0);
      setUploading(false);
      // Refresh folder data after upload
      await queryClient.invalidateQueries({ queryKey: ["categoryFolders"] });
      // Refetch folders to get updated data
      const folders = await queryClient.fetchQuery({
        queryKey: ["categoryFolders"],
      });
      // Find updated folder and update selected folder state in parent
      const updatedFolder = folders.find((f) => f.folderId === folder.folderId);
      if (updatedFolder && onFolderUpdate) {
        onFolderUpdate(updatedFolder);
      }
    } catch (error) {
      setUploadError(error.message || "Upload failed");
      setUploading(false);
    }
  };

  const gridSizes = getGridSizes();

  // New handlers for preview
  const handlePreviewOpen = (file) => {
    if (file) {
      setPreviewFile(file);
      setPreviewOpen(true);
    }
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
  };

  // Removed handleToggleSummary as summary state is removed

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleUploadClick}
          disabled={uploading}
        >
          Upload File
        </Button>
        <Box sx={{ marginLeft: "auto" }}>
          <ViewSizeSelector viewSize={viewSize} setViewSize={setViewSize} />
        </Box>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        {uploading && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}
        {uploadError && (
          <Typography color="error" variant="body2">
            {uploadError}
          </Typography>
        )}
      </Box>
      {files.length === 0 ? (
        <Typography>No files found in this folder.</Typography>
      ) : viewSize === "small" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {files.map((file) => (
            <FileCard
              key={file.storagePath}
              file={file}
              onPreview={() => handlePreviewOpen(file)}
              onDelete={() => handleDeleteRequest(file)}
              viewSize={viewSize}
            />
          ))}
        </Box>
      ) : (
        <Grid container spacing={2}>
          {files.map((file) => (
            <Grid item key={file.storagePath} {...gridSizes}>
              <FileCard
                file={file}
                onPreview={() => handlePreviewOpen(file)}
                onDelete={() => handleDeleteRequest(file)}
                viewSize={viewSize}
              />
            </Grid>
          ))}
        </Grid>
      )}
      <FilePreviewDataset
        open={previewOpen}
        onClose={handlePreviewClose}
        file={previewFile}
      />
      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Delete"
        message="Are you sure you want to delete this file? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
}
